import React, {useCallback, useState} from "react"
import {IncidentFeedSelector} from "@/components/paginated-selector/incident-feed-selector";
import {Link, useNavigate} from "react-router-dom";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";
import {apiV2} from "@/api/axiosConfig";
import {ConfirmationModal} from "@/components/modals/ConfirmationModal";
import {Action, ActionSelect} from "@/components/ui/ActionSelect";
import {useCopyID} from "@/lib/misc/useCopyID";

type IncidentFeed = {
    id: string;
    name: string;
    clientID: string;
    clientName: string;
    type: string;
    reference: string;
}

export function IncidentFeedListPage() {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const {copyID, isCopied} = useCopyID();
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [feedToDelete, setFeedToDelete] = useState<IncidentFeed | null>(null);

    const handleDeleteClick = useCallback((feed: IncidentFeed) => {
        setFeedToDelete(feed);
        setDeleteModalOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!feedToDelete) return;

        try {
            await apiV2.delete(`incident-feeds/${feedToDelete.id}`);
            setRefreshKey(prev => prev + 1);
            setFeedToDelete(null);
        } catch (error) {
            console.error("Failed to delete incident feed:", error);
            alert("Failed to delete incident feed");
        }
    }, [feedToDelete]);

    function feedsUrl() {
        return window.location.pathname + window.location.search;
    }

    const handleColumnClick = useCallback((feed: IncidentFeed, columnIndex: number) => {
        // Column 0 is the feed name
        if (columnIndex === 0) {
            navigate(`/incident-feeds/edit/${feed.id}?prev=${encodeURIComponent(feedsUrl())}`);
        }

        if(columnIndex === 1) {
            window.open(`/management/clients/edit/${feed.clientID}`, "_blank");
        }
    }, []);

    const renderActions = useCallback((feed: IncidentFeed) => {

        return <ActionSelect label={"Select Action"}>
            <Action label={"Delete"} onSelect={() => handleDeleteClick(feed)}/>
            <Action label={isCopied(feed.id) ? "Copied!" : "Copy ID"} onSelect={() => copyID(feed.id)} keepOpen />
        </ActionSelect>
    }, [handleDeleteClick, copyID, isCopied]);

    return (
        <div className="flex flex-col gap-4">
            <nav className="flex items-center gap-2 text-sm text-light-grey">
                <Link to="/sites" className="hover:text-grey">Sites</Link>
                <span>&gt;</span>
                <span className="text-grey">Incident Feeds</span>
            </nav>
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-semibold">Incident Feeds</h1>
                <div className="flex-shrink-0">
                    <ButtonV2
                        label="Create Incident Feed"
                        onClick={() => navigate(`/incident-feeds/create?prev=${encodeURIComponent(feedsUrl())}`)}
                    />
                </div>
            </div>
            <div className="">
                <IncidentFeedSelector
                    key={refreshKey}
                    renderActions={renderActions}
                    onColumnClick={handleColumnClick}
                />
            </div>

            <ConfirmationModal
                open={deleteModalOpen}
                setOpen={setDeleteModalOpen}
                title="Delete Incident Feed"
                message={`Are you sure you want to delete "${feedToDelete?.name}"? This action cannot be undone.`}
                confirmLabel="Delete"
                cancelLabel="Cancel"
                onConfirm={handleDeleteConfirm}
            />
        </div>
    )
}
